import { useSyncExternalStore } from "react";

import { dropWindowGrants } from "./worldMcp";

/**
 * cleo-web (webHost.ts): what the browser host knows of its sign-in, published by WebHostWorldSession.tsx and read by the
 * screens (components/webHost/WebHostLanding.tsx). The Desk's equivalent is the server's `live` report (worldClerk.ts);
 * here there is no server, so the window keeps it itself.
 */
export type WebHostSessionState =
  | { readonly state: "loading" }
  | { readonly state: "no-sign-in"; readonly reason: string }
  | { readonly state: "signed-out" }
  | { readonly state: "checking"; readonly identity: string | null }
  | {
      readonly state: "accepted";
      readonly identity: string | null;
      readonly actorId: string;
      readonly worlds: ReadonlyArray<{ readonly worldKey: string; readonly name?: string }>;
      readonly checkedAt: string;
    }
  | {
      readonly state: "refused";
      readonly identity: string | null;
      /** The sentence the landing shows (webHostSignInRefusal.ts). */
      readonly reason: string;
      /** What the refusal means: not enrolled in any World, the World unavailable, or another refusal in its words. */
      readonly kind: "not-enrolled" | "unavailable" | "other";
      /** The World's own sentence, for the small print and the test-identity rail. */
      readonly worldWords: string;
    };

let current: WebHostSessionState = { state: "loading" };
const listeners = new Set<() => void>();

export function publishWebHostSession(next: WebHostSessionState): void {
  // The window's own World grants (worldMcp.ts) are the signed-in person's: a sign-out or another person drops them.
  const before = current.state === "accepted" ? current.actorId : null;
  const after = next.state === "accepted" ? next.actorId : null;
  if (before !== null && before !== after) void dropWindowGrants();
  current = next;
  for (const listener of listeners) listener();
}

export function readWebHostSession(): WebHostSessionState {
  return current;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export function useWebHostSession(): WebHostSessionState {
  return useSyncExternalStore(subscribe, readWebHostSession, readWebHostSession);
}
